import { useCallback } from 'react'
import { useGlobalState } from 'context/GlobalStateContext'
import { simpleEditNotification } from 'hooks/notificationHandler'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { useRecipientsSelectContext } from 'components/CIMSSelect/common/RecipientsSelect/RecipientsSelectContext'
import { useDSNotificationsContext } from '../../DSNotificationsContext'
import { EditNotificationEventOptionsFn } from 'components/CIMSNotifications/types'
import { Permission } from 'shared'
import { LabeledValueType } from 'components/CIMSSelect/types'
import RenderFormItems from './components/RenderFormItems'
import { DisciplineFormValue } from './types'
import { EditDsNotificationInput, useEditDsNotificationMutation } from 'schema'
import { errorHandler } from 'hooks/errorHandler'

export function useEditFn() {
  const {
    state: { projectGlobalId: projectId },
  } = useGlobalState()
  const { recipients, refetch } = useDSNotificationsContext()
  const { toLabeledValues, fromLabeledValues } = useRecipientsSelectContext()
  const [edit] = useEditDsNotificationMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      editDSNotification: {
        notification: { notificationEvent },
      },
    }) => simpleEditNotification(notificationEvent),
  })
  return useCallback<EditNotificationEventOptionsFn<InitialValues>>(
    (event) => {
      const { every, byDSDiscipline } = recipients(event)
      return {
        render: <RenderFormItems />,
        initialValues: {
          recipients: every ? toLabeledValues(every) : [],
          dsDiscipline: byDSDiscipline?.map(({ discipline, recipients }) => ({
            dsDisciplineId: discipline.id,
            recipients: toLabeledValues(recipients),
          })),
        },
        permission: Permission.EditDSNotification,
        onSave: async ({ recipients, dsDiscipline }) => {
          if (!projectId) return errorHandler('Project id must be defined')

          const input: EditDsNotificationInput = {
            notificationEventId: event.id,
            projectId,
            every: fromLabeledValues(recipients),
            byDSDiscipline: dsDiscipline?.map(({ recipients, ...rest }) => ({
              ...rest,
              recipients: fromLabeledValues(recipients),
            })),
          }

          await edit({ variables: { editDsNotificationInput: input } })
          refetch()
        },
        onClear: async () => {
          if (!projectId) return errorHandler('Project id must be defined')

          await edit({
            variables: {
              editDsNotificationInput: {
                notificationEventId: event.id,
                projectId,
                every: { userIds: [], nonUserEmailIds: [] },
                byDSDiscipline: [],
              },
            },
          })
        },
      }
    },
    [edit, recipients, projectId, refetch, toLabeledValues, fromLabeledValues]
  )
}

interface InitialValues {
  recipients: LabeledValueType[]
  dsDiscipline?: DisciplineFormValue[]
}
